import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TablePagination, Avatar, IconButton, CircularProgress, Select, MenuItem, FormControl, Tooltip,
  CardActions, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Safe SERVER_URI fallback for browser runtime
const SERVER_URI = process.env.REACT_APP_SERVER_URI;

export default function CarList() {
  const [cars, setCars] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // zero-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({});
  const navigate = useNavigate();

  // Allowed status options
  const STATUS_OPTIONS = [
    'SUBSCRIBED', 'cancelled', 'ASK FOR PLAN', 'CONVERT CURRENT WASHER', 'demo done',
    'no service required', 'has regular guy', 'DEMO SCHEDULED', 'FOLLOW UP', 'NO RESPONSE',
    'ON DEMAND', 'PRICE REQUESTED', 'RAMESH', 'SEND SUB. DET.', 'WASHER N/A', 'WAITING LIST',
    'NOT NEEDED', 'CAR SOLD'
  ];

  // helper to format dates as DD-MM-YYYY hh:mm AM/PM
  const formatDate = (input) => {
    if (!input) return '-';
    const d = new Date(input);
    if (isNaN(d)) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}-${month}-${year} ${String(hours).padStart(2,'0')}:${mins} ${ampm}`;
  };

  const fetchCars = async (pageIndex = page, limit = rowsPerPage) => {
    setLoading(true);
    setError(null);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    console.log(statusFilter);
    try {
      const res = await fetch(`${SERVER_URI}/cars/list?page=${pageIndex + 1}&limit=${limit}&status=${statusFilter}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });
      if (!res.ok) throw new Error(`Failed to fetch cars (${res.status})`);
      const body = await res.json();
      console.log('Fetched cars:', body);
      // expected shape: { data: [...], total: number }
      setCars(body.cars || body || []);
      setTotal(typeof body.total === 'number' ? body.total : (Array.isArray(body) ? body.length : 0));
    } catch (err) {
      setError(err.message || 'Failed to load cars');
      setCars([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async (carId, newStatus) => {
    if (!window.confirm('Do you want to update the status?')) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    // optimistic update
    setCars(prev => prev.map(c => c._id === carId ? { ...c, status: newStatus } : c));
    try {
      const res = await fetch(`${SERVER_URI}/cars/update-status/${carId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error(`Status update failed (${res.status})`);
      // optionally read response
    } catch (err) {
      // revert on error
      setCars(prev => prev.map(c => c._id === carId ? { ...c, status: prev.find(p=>p._id===carId)?.status || 'inactive' } : c));
      alert('Failed to update status');
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Delete this car? This action cannot be undone.')) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      const res = await fetch(`${SERVER_URI}/cars/${carId}`, {
        method: 'DELETE',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setCars(prev => prev.filter(c => c._id !== carId));
      setTotal(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error(err);
      alert('Failed to delete car');
    }
  };

  const handleOpenDialog = (car) => {
    const customer = car.ownerName || car.name || 'N/A';
    const phone = car.ownerPhone || car.phone || car.contact || 'N/A';
    const plan = car.plan || (car.subscription && (car.subscription.plan || car.subscription.name)) || 'N/A';
    const startDate = formatDate(car.subscriptionExpiryDate || car.startDate || car.subscription?.startDate || car.start_date);
    const expireDate = formatDate(car.subscriptionExpiryDate || car.subscriptionExpiry || car.subscription?.expiryDate || car.subscriptionExpiryDate);
    const status = car.status || 'N/A';
    const washType = car.washType || car.wash_type || 'N/A';
    setDialogData({ customer, phone, plan, startDate, expireDate, status, washType });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogData({});
  };

  useEffect(() => {
    fetchCars(0, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchCars(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    fetchCars(0, newLimit);
  };

  return (
    <Box>
      {/* Header and Filters */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Followup</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">All Statuses</MenuItem>
              {STATUS_OPTIONS.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={() => fetchCars(page, rowsPerPage)} aria-label="refresh">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>
        <Paper>
          <TableContainer>
            <Table size="small">
          <TableHead>
            <TableRow>
              {['Image', 'Car Name', 'Reg No', 'Type', 'User', 'Status', 'Subscription Status', 'Expiry date', 'Actions'].map(header => (
            <TableCell key={header}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
            <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
              <CircularProgress />
            </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
            <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'error.main' }}>
              {error}
            </TableCell>
              </TableRow>
            ) : cars.length === 0 ? (
              <TableRow>
            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
              No cars found.
            </TableCell>
              </TableRow>
            ) : (
              cars.map((car) => (
            <TableRow key={car._id || car.regNo}>
              <TableCell>
                <Avatar src={car.imageUrl} variant="rounded">
              {!car.imageUrl && (car.model || 'C')[0]}
                </Avatar>
              </TableCell>
              <TableCell>
                {car.model || '-'}<br />
                {car.name || '-'}
              </TableCell>
              <TableCell>{car.regNo || '-'}</TableCell>
              <TableCell>{car.type || '-'}</TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={0.5}>
              <Tooltip title="Open owner page">
                <span
                  style={{
                cursor: car.owner ? 'pointer' : 'default',
                color: car.owner ? '#1976d2' : undefined,
                textDecoration: car.owner ? 'underline' : undefined
                  }}
                  onClick={() => car.owner && handleOpenDialog(car)}
                >
                  {car.ownerName || '-'}
                </span>
              </Tooltip>
              {car.owner && (
                <Tooltip title="Go to owner details">
                  <IconButton
                size="small"
                onClick={() => navigate(`/users/${car.owner}`)}
                sx={{ ml: 0.5 }}
                  >
                <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
                </Box>
              </TableCell>
              <TableCell>
                <FormControl size="small" fullWidth>
              <Select
                value={car.status || 'inactive'}
                onChange={(e) => handleChangeStatus(car._id, e.target.value)}
              >
                {STATUS_OPTIONS.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                {car.subscriptionId ? (
              <a
                target='_blank'
                rel="noreferrer"
                href={`/subscriptions/${car.subscriptionId}`}
                style={{
                  color: car.subscriptionStatus === 'active' ? 'green' : 'red',
                  fontWeight: 600,
                  textDecoration: 'underline'
                }}
              >
                {car.subscriptionStatus === 'active' ? 'Subscribed' : 'Expired'}
              </a>
                ) : car.freewashCompleted ? (
                  <>Free wash completed</>
                ) : car.freeWashRequest ? (
                  <>Free wash requested</>
                ) : (
                  <span style={{ color: 'red', fontWeight: 600 }}>Not Subscribed</span>
                )}
              </TableCell>
              <TableCell>
                {car.subscriptionExpiryDate
              ? new Date(car.subscriptionExpiryDate).toLocaleDateString()
              : '-'}
              </TableCell>
              <TableCell>
                <Tooltip title="Delete car">
              <IconButton size="small" color="error" onClick={() => handleDeleteCar(car._id)}>
                <DeleteIcon />
              </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
              ))
            )}
          </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>

        {/* Customer detail dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Customer Details</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1}>
            {[
              { label: 'Customer', value: dialogData.customer },
              { label: 'Phone', value: dialogData.phone },
              { label: 'Plan', value: dialogData.plan },
              { label: 'Start Date', value: dialogData.startDate },
              { label: 'Expire Date', value: dialogData.expireDate },
            ].map(({ label, value }) => (
              <Grid item xs={12} key={label}>
                <Typography variant="subtitle2">{label}:</Typography>
                <Typography variant="body1">{value}</Typography>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
